<template>
  <div class="chat-notification-container" v-if="notifications.length > 0">
    <TransitionGroup name="slide-down">
      <div
        v-for="notif in notifications"
        :key="notif.id"
        class="chat-notification"
        @click="openChat(notif)"
      >
        <div class="notification-avatar">
          <MessageDots />
        </div>
        <div class="notification-content">
          <div class="notification-title">
            <span class="order-id">#{{ extractOrderId(notif.orderId) }}</span>
            <span class="sender-label">{{ notif.sender === 'boss' ? '店长' : '打手' }}</span>
          </div>
          <div class="notification-text">{{ notif.text }}</div>
          <div class="notification-time">{{ formatTime(notif.timestamp) }}</div>
        </div>
        <div class="notification-actions">
          <div class="notification-hint">点击进入聊天</div>
          <div class="notification-close" @click.stop="removeNotification(notif.id)">
            <X size="16" />
          </div>
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { MessageDots, X } from 'lucide-vue-next'

const emit = defineEmits(['open-chat'])

const notifications = ref([])
const notificationIdCounter = ref(0)

// 提取订单ID（处理 xianyu_77 这样的格式）
const extractOrderId = (orderId) => {
  if (!orderId) return ''
  const str = String(orderId)
  if (str.startsWith('xianyu_')) {
    return str.replace('xianyu_', '')
  }
  if (str.startsWith('orders_')) {
    return str.replace('orders_', '')
  }
  return str
}

// 添加通知
const addNotification = (orderId, message) => {
  console.log('[ChatNotification] 添加通知:', orderId, message)
  // 检查是否已经存在该订单的通知（避免重复）
  const existingNotif = notifications.value.find(n => n.orderId === orderId)
  if (existingNotif) {
    // 更新现有通知的内容
    existingNotif.text = message.text
    existingNotif.timestamp = message.timestamp
    return
  }

  const id = ++notificationIdCounter.value

  notifications.value.unshift({
    id,
    orderId,
    ...message
  })
}

// 监听 window 事件以显示通知
function handleWindowNotification(e) {
  console.log('[ChatNotification] 收到 window 事件通知:', e.detail)
  const { orderId, message } = e.detail
  if (orderId && message) {
    addNotification(orderId, message)
  }
}

onMounted(() => {
  console.log('[ChatNotification] 组件挂载，开始监听 chat-notification 事件')
  window.addEventListener('chat-notification', handleWindowNotification)
})

onUnmounted(() => {
  window.removeEventListener('chat-notification', handleWindowNotification)
})

// 移除通知
const removeNotification = (id) => {
  const index = notifications.value.findIndex(n => n.id === id)
  if (index !== -1) {
    notifications.value.splice(index, 1)
  }
}

// 打开聊天
const openChat = (notif) => {
  emit('open-chat', notif.orderId)
  removeNotification(notif.id)
}

// 格式化时间
const formatTime = (timestamp) => {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  return date.toLocaleTimeString('zh-CN', {
    hour: '2-digit',
    minute: '2-digit'
  })
}

defineExpose({
  addNotification
})
</script>

<style scoped>
.chat-notification-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 12px;
  pointer-events: none;
}

.chat-notification {
  pointer-events: auto;
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.3), rgba(168, 85, 247, 0.2));
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(168, 85, 247, 0.4);
  border-radius: 16px;
  padding: 16px 18px;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  min-width: 340px;
  max-width: 400px;
  box-shadow: 0 10px 40px rgba(139, 92, 246, 0.35), 0 4px 12px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  transition: all 0.3s ease;
}

.chat-notification:hover {
  transform: translateX(-6px);
  border-color: rgba(168, 85, 247, 0.7);
  box-shadow: 0 14px 48px rgba(139, 92, 246, 0.45), 0 6px 16px rgba(0, 0, 0, 0.25);
}

.notification-avatar {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: linear-gradient(135deg, #8b5cf6, #a855f7);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.order-id {
  font-size: 14px;
  font-weight: 700;
  color: #c4b5fd;
}

.sender-label {
  font-size: 12px;
  padding: 3px 10px;
  border-radius: 12px;
  background: rgba(139, 92, 246, 0.25);
  color: #ddd6fe;
  font-weight: 600;
}

.notification-text {
  font-size: 15px;
  color: #f5f3ff;
  line-height: 1.5;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
}

.notification-time {
  font-size: 12px;
  color: #a78bfa;
  margin-top: 6px;
  font-weight: 500;
}

.notification-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.notification-hint {
  font-size: 11px;
  color: rgba(196, 181, 253, 0.7);
  white-space: nowrap;
}

.notification-close {
  width: 28px;
  height: 28px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a78bfa;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
  background: rgba(139, 92, 246, 0.15);
}

.notification-close:hover {
  background: rgba(139, 92, 246, 0.3);
  color: #f5f3ff;
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.4s ease;
}

.slide-down-enter-from {
  opacity: 0;
  transform: translateX(100px) scale(0.9);
}

.slide-down-leave-to {
  opacity: 0;
  transform: translateX(50px) scale(0.95);
}

.slide-down-move {
  transition: transform 0.3s ease;
}
</style>
